<template>
  <div class="flex items-start space-x-3">
    <div class="flex items-center space-x-3">
      <AirlineIconsGroup
        :airlines="uniqueAirlines"
        :airlines-to-display="2"
        is-vertical
      />
      <div>
        <div class="flex w-28 items-center justify-between gap-2">
          <div class="text-text-neutral-primary flex flex-col">
            <p
              class="h-fit whitespace-nowrap text-base"
            >
              {{ flightDepTime }}
            </p>
            <span class="text-situational-grey text-xs">
              {{ firstSegment.dep.airport }}
            </span>
          </div>
          <p class="text-situational-grey text-xs">—</p>
          <div class="flex flex-col">
            <p
              class="text-text-neutral-primary h-fit whitespace-nowrap text-base"
            >
              {{ flightArrTime }}
            </p>
            <span class="text-situational-grey text-xs">
              {{ lastSegment.arr.airport }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div>
      <p class="text-text-neutral-primary mb-0.5 whitespace-nowrap text-sm">
        <span
        >
          {{ transfersText }}
        </span>
        /
        <span
        >
          {{ flight.durationText }}
        </span>
      </p>

      <div v-for="(segment, idx) in flight.segments" :key="idx">
        <div v-if="segment.stops.count">
          <p
            v-for="location in segment.stops.locations"
            :key="location.airport"
            class="text-situational-grey text-xs"
          >
            {{ location.duration }} {{ $t('main.cities.stop') }}
          </p>
        </div>
        <p
          v-if="segmentsData.isFlightConnectionVisible(idx)"
          class="text-situational-grey text-xs"
        >
          {{
            getDurationBetweenSegments(
              segment.arr,
              segmentsData.getNextSegmentDep(idx),
            )
          }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import type { Airflow } from '@aviata/platform-types';
  import { AirlineIconsGroup } from 'freedom-shared';
  import { ticketDetails, dateUtils } from 'shared';

  const props = withDefaults(
    defineProps<{
      offerIdx: number;
      flightIdx: number;
      flight: ticketDetails.types.MappedFlight;
      airlines?: ticketDetails.types.ExpandedAirline[];
    }>(),
    { airlines: () => [{ iata: 'KC', title: 'Air' }] },
  );

  const { t } = useI18n();

  const firstSegment = computed(() => props.flight.segments[0]);
  const lastSegment = computed(() => props.flight.segments.slice(-1)[0]);
  const flightDepTime = computed(() => firstSegment.value.dep.time);
  const flightArrTime = computed(() => lastSegment.value.arr.time);
  const numberOfTransfers = computed(() => props.flight.segments.length - 1);
  const transfersText = computed(() =>
    numberOfTransfers.value > 0
      ? t('general.transfers', numberOfTransfers.value)
      : t('general.direct'),
  );
  const uniqueAirlines = computed(() =>
    ticketDetails.lib.getUniqueAirlines(props.flight),
  );
  const segmentsData = computed(() =>
    ticketDetails.lib.getSegmentsData(props.flight),
  );

  function getDurationBetweenSegments(
    currentSegmentArr: Airflow.Segment.Point,
    nextSegmentDep: Airflow.Segment.Point | null,
  ) {
    if (!currentSegmentArr || !nextSegmentDep) return;

    const arrAt = new Date(currentSegmentArr.at);
    const depAt = new Date(nextSegmentDep.at);
    const durationInSeconds = (depAt.getTime() - arrAt.getTime()) / 1000;
    const duration = dateUtils.prettyDurationFromSeconds(durationInSeconds);

    return [duration, nextSegmentDep.city].filter(e => e).join(' ');
  }
</script>
